import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePatientFlow } from '../context/PatientFlowContext';
import { api } from '../utils/api';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/Toaster';
import { ChevronRight, ChevronLeft, CheckCircle, Upload, X, Shield, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import DateField from '../components/DateField';
import { useTranslation } from 'react-i18next';

const PatientIntakeEnhanced = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateFlowState } = usePatientFlow();
  const [searchParams] = useSearchParams();
  const treatmentParam = searchParams.get('treatment');
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingIntake, setExistingIntake] = useState(null);
  const [isNewQuoteRequest, setIsNewQuoteRequest] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  // Canonical residence (persons.residence_country) — distinct from country_of_origin.
  const [residenceCountry, setResidenceCountry] = useState('');
  useEffect(() => {
    if (!user) return;
    api.get('/surface/residence')
      .then(({ data }) => { if (data?.declared && data.residence_country) setResidenceCountry(data.residence_country); })
      .catch(() => {});
  }, [user]);
  
  // GDPR Consent State
  
  // Validation State
  const [validationErrors, setValidationErrors] = useState({});
  const [attemptedNext, setAttemptedNext] = useState(false);
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    age: '',
    gender: '',
    country_of_origin: '',
    email: '',
    
    // Treatment Selection
    treatment_categories: [],
    treatment_details: '',
    previous_surgeries: '',
    expectations: '',
    
    // Health Questions
    smoking_status: '',
    chronic_diseases: [],
    medications: '',
    bmi: '',
    allergies: '',
    
    // Travel & Package Preferences
    package_type: [],
    earliest_date: '',
    latest_date: '',
    days_available: '',
    preferred_country: '',
    preferred_city: '',
    budget_range: '',
    
    // Photos (mock storage - stored as base64 for MVP)
    photos: []
  });

  const totalSteps = 7; // Increased from 6 to 7

  useEffect(() => {
    checkExistingIntake();
    // Check for preselected treatment from session storage
    const preselectedTreatment = sessionStorage.getItem('preselectedTreatment');
    if (preselectedTreatment) {
      // Map treatment name to category
      const treatmentMapping = {
        'Mommy Makeover': 'Mommy Makeover',
        'Hair Transplant': 'Hair Transplant (FUE/DHI)',
        'Rhinoplasty': 'Facial Aesthetics (Rhinoplasty, Facelift, Eyelids)',
        'Dental Implants': 'Dental (Implants, Veneers, All-on-4)',
        'Breast Augmentation': 'Breast Surgery (Augmentation, Lift, Reduction)',
        'Liposuction': 'Body Contouring (Tummy Tuck, Liposuction, BBL)',
        'Tummy Tuck': 'Body Contouring (Tummy Tuck, Liposuction, BBL)',
      };
      
      // Find matching category
      let matchedCategory = '';
      for (const [key, value] of Object.entries(treatmentMapping)) {
        if (preselectedTreatment.toLowerCase().includes(key.toLowerCase())) {
          matchedCategory = value;
          break;
        }
      }
      
      if (matchedCategory) {
        setFormData(prev => ({
          ...prev,
          treatment_categories: [matchedCategory]
        }));
      }
      
      // Clear session storage after use
      sessionStorage.removeItem('preselectedTreatment');
    }
  }, []);

  const checkExistingIntake = async () => {
    if (!user) return;
    
    try {
      const response = await api.get(`/patient/onboarding/${user.id}`);
      if (response.data && response.data.id) {
        setExistingIntake(response.data);
        // Pre-fill data EXCEPT gender (as per spec); drop nulls so controlled inputs/validators stay string-safe
        const clean = Object.fromEntries(Object.entries(response.data).filter(([, v]) => v !== null && v !== undefined));
        setFormData(prev => ({
          ...prev,
          ...clean,
          gender: prev.gender // Keep gender as user originally chose
        }));
        setIsNewQuoteRequest(true);
      }
    } catch (error) {
      // No existing intake - this is a new patient
      console.log('No existing intake found - new patient');
    }
  };

  // Step 1: Basic Info
  const genderOptions = [
    { value: 'Woman', label: 'Woman', icon: '👩' },
    { value: 'Man', label: 'Man', icon: '👨' },
    { value: 'Non-binary', label: 'Non-binary', icon: '🧑' },
    { value: 'Prefer not to say', label: 'Prefer not to say', icon: '❓' },
  ];

  // Step 2: Treatment Details
  const treatmentCategories = [
    'Mommy Makeover',
    'Breast Surgery (Augmentation, Lift, Reduction)',
    'Body Contouring (Tummy Tuck, Liposuction, BBL)',
    'Hair Transplant (FUE/DHI)',
    'Facial Aesthetics (Rhinoplasty, Facelift, Eyelids)',
    'Dental (Implants, Veneers, All-on-4)',
    'Gynecomastia',
    'Male Enhancement',
    'Facial Feminization/Masculinization',
    'Other'
  ];

  // Step 3: Health Questions
  const smokingOptions = [
    { value: 'non-smoker', label: 'Non-smoker' },
    { value: 'occasional', label: 'Occasional smoker' },
    { value: 'regular', label: 'Regular smoker' },
    { value: 'quit', label: 'Quit smoking' }
  ];

  const chronicDiseaseOptions = [
    'Diabetes',
    'High Blood Pressure',
    'Heart Disease',
    'Asthma',
    'Thyroid Disorder',
    'Autoimmune Disease',
    'None'
  ];

  // Step 4: Package Type & Travel Preferences
  const packageTypes = [
    { 
      value: 'treatment-only', 
      label: 'Treatment Only', 
      desc: 'Medical procedure only, I\'ll arrange my own accommodation',
      icon: '🏥' 
    },
    { 
      value: 'treatment-tourism', 
      label: 'Treatment + Tourism Package', 
      desc: 'Includes hotel, airport transfers, and treatment',
      icon: '🏖️' 
    },
    { 
      value: 'vip', 
      label: 'VIP Package', 
      desc: 'Premium service with concierge, private transfers, luxury hotel',
      icon: '⭐' 
    }
  ];

  const [countries, setCountriesList] = useState([]);
  const [clinicCountriesList, setClinicCountriesList] = useState([]);

  useEffect(() => {
    api.get(`/public/catalog/countries`)
      .then(({ data }) => data)
      .then(d => { if (d.success) setCountriesList(d.countries); })
      .catch(() => {});
    api.get(`/public/catalog/clinic-countries`)
      .then(({ data }) => data)
      .then(d => { if (d.success) setClinicCountriesList(d.countries); })
      .catch(() => {});
  }, []);

  const budgetRanges = [
    { value: '0-2500', label: 'Under €2,500' },
    { value: '2500-5000', label: '€2,500 - €5,000' },
    { value: '5000-10000', label: '€5,000 - €10,000' },
    { value: '10000-20000', label: '€10,000 - €20,000' },
    { value: '20000+', label: 'Over €20,000' }
  ];

  // Step 5: Photo Upload
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 5000000) { // 5MB limit
        toast({
          title: 'File too large',
          description: `${file.name} is larger than 5MB`,
          variant: 'destructive'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedPhotos(prev => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            name: file.name,
            dataUrl: reader.result
          }
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const toggleTreatment = (treatment) => {
    setFormData(prev => ({
      ...prev,
      treatment_categories: prev.treatment_categories.includes(treatment)
        ? prev.treatment_categories.filter(t => t !== treatment)
        : [...prev.treatment_categories, treatment]
    }));
  };

  const toggleChronicDisease = (disease) => {
    if (disease === 'None') {
      setFormData(prev => ({ ...prev, chronic_diseases: ['None'] }));
    } else {
      setFormData(prev => ({
        ...prev,
        chronic_diseases: prev.chronic_diseases.includes(disease)
          ? prev.chronic_diseases.filter(d => d !== disease)
          : prev.chronic_diseases.filter(d => d !== 'None').concat(disease)
      }));
    }
  };

  // Validation functions
  const validateStep = (stepNum) => {
    const errors = {};
    
    if (stepNum === 1) {
      if (!(formData.name || '').trim()) errors.name = 'Full name is required';
      if (!formData.age) errors.age = 'Age is required';
      else if (parseInt(formData.age) < 18 || parseInt(formData.age) > 100) errors.age = 'Please enter a valid age (18-100)';
      if (!formData.gender) errors.gender = 'Please select your gender';
      if (!residenceCountry) errors.residence_country = t('residencePrompt.required');
      if (!formData.country_of_origin) errors.country_of_origin = t('residencePrompt.originRequired');
    }
    
    if (stepNum === 2) {
      if (formData.treatment_categories.length === 0) errors.treatment_categories = 'Please select at least one treatment';
    }
    
    if (stepNum === 3) {
      if (!formData.smoking_status) errors.smoking_status = 'Please select your smoking status';
      if (formData.chronic_diseases.length === 0) errors.chronic_diseases = 'Please select at least one option (or "None")';
    }
    
    if (stepNum === 4) {
      if (formData.package_type.length === 0) errors.package_type = 'Please select at least one package type';
    }
    
    return errors;
  };

  const isStepValid = useMemo(() => {
    const errors = validateStep(step);
    return Object.keys(errors).length === 0;
  }, [step, formData, residenceCountry]);

  const handleNext = () => {
    setAttemptedNext(true);
    const errors = validateStep(step);
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast({
        title: 'Required fields missing',
        description: 'Please fill in all required fields highlighted in red',
        variant: 'destructive'
      });
      return;
    }

    if (step < totalSteps) {
      setStep(step + 1);
      setAttemptedNext(false);
      setValidationErrors({});
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setAttemptedNext(false);
      setValidationErrors({});
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Canonical residence FIRST — commercial request cannot proceed without it.
      if (!residenceCountry) {
        toast({ title: 'Error', description: t('residencePrompt.required'), variant: 'destructive' });
        setStep(1); setAttemptedNext(true); setLoading(false);
        return;
      }
      try {
        await api.post('/surface/residence', { residence_country: residenceCountry });
      } catch (resErr) {
        toast({ title: 'Error', description: t('residencePrompt.error'), variant: 'destructive' });
        setLoading(false);
        return;
      }
      // Prepare data with photos
      const submitData = {
        ...formData,
        user_id: user.id,
        photos: uploadedPhotos.map(p => ({ name: p.name, data: p.dataUrl })),
      };

      const response = await api.post('/patient/onboarding', submitData);
      
      // Update flow state
      updateFlowState({
        intakeCompleted: true,
        currentStep: 'intake-complete'
      });
      
      toast({
        title: 'Success!',
        description: isNewQuoteRequest 
          ? 'New quotation request created successfully. Now select clinics!' 
          : 'Your profile is complete! Now let\'s find the perfect clinics for you.',
      });

      // Automatically redirect to clinic selection with all preferences
      setTimeout(() => {
        // Store intake preferences in sessionStorage for clinic search
        sessionStorage.setItem('intakePreferences', JSON.stringify({
          treatment: formData.treatment_categories[0] || '',
          package: Array.isArray(formData.package_type) ? formData.package_type.join(', ') : formData.package_type || '',
          country: formData.preferred_country || '',
          budget_range: formData.budget_range || ''
        }));
        
        navigate('/patient/clinic-search?' + new URLSearchParams({
          treatment: formData.treatment_categories[0] || '',
          package: Array.isArray(formData.package_type) ? formData.package_type.join(', ') : formData.package_type || '',
          country: formData.preferred_country || ''
        }).toString());
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting intake:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save your information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / totalSteps) * 100;

  // Helper function for error styling
  const getFieldErrorClass = (fieldName) => {
    if (attemptedNext && validationErrors[fieldName]) {
      return 'border-red-500 ring-2 ring-red-200';
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12">
      <Toaster />
      
      {/* The `patient_gdpr_consent` ConsentGate was removed together with its
          retired CMS record (TREATX-LEGAL-CORPUS-CLEANUP-AC-01). Consent for
          this journey is governed by the binding-driven interceptor. */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isNewQuoteRequest ? 'New Treatment Request' : 'Complete Your Profile'}
          </h1>
          <p className="text-gray-600">
            {isNewQuoteRequest 
              ? 'Update your preferences for this new quotation request'
              : 'Help us find the perfect clinic and treatment for you'
            }
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-gray-600 mt-2 text-center">
            Step {step} of {totalSteps}
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-6 sm:p-8">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Basic Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    className={getFieldErrorClass('name')}
                  />
                  {attemptedNext && validationErrors.name && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {validationErrors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Age *</label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    placeholder="Your age"
                    className={getFieldErrorClass('age')}
                  />
                  {attemptedNext && validationErrors.age && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {validationErrors.age}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Gender Identity *</label>
                  <div className={`grid grid-cols-2 gap-3 ${attemptedNext && validationErrors.gender ? 'ring-2 ring-red-200 rounded-lg p-1' : ''}`}>
                    {genderOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, gender: option.value })}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          formData.gender === option.value
                            ? 'border-blue-600 bg-blue-50'
                            : attemptedNext && validationErrors.gender
                              ? 'border-red-300 hover:border-red-400'
                              : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="text-2xl mb-2">{option.icon}</div>
                        <div className="font-medium">{option.label}</div>
                      </button>
                    ))}
                  </div>
                  {attemptedNext && validationErrors.gender && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {validationErrors.gender}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" data-testid="intake-residence-label">{t('residencePrompt.label')} *</label>
                  <select
                    value={residenceCountry}
                    onChange={(e) => setResidenceCountry(e.target.value)}
                    className={`w-full p-3 border rounded-lg ${attemptedNext && validationErrors.residence_country ? 'border-red-500' : ''}`}
                    data-testid="intake-residence-select"
                  >
                    <option value="">{t('residencePrompt.placeholder')}</option>
                    {countries.map(country => (
                      <option key={`res-${country.code || country}`} value={country.code || country.name || country}>{country.name || country}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">{t('residencePrompt.hint')}</p>
                  {(!residenceCountry || (attemptedNext && validationErrors.residence_country)) && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1" data-testid="intake-residence-error">
                      <AlertCircle className="h-4 w-4" />
                      {t('residencePrompt.required')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" data-testid="intake-origin-label">{t('residencePrompt.originLabel')} * <span className="text-gray-400 font-normal">— {t('residencePrompt.originHint')}</span></label>
                  <select
                    value={formData.country_of_origin}
                    onChange={(e) => setFormData({ ...formData, country_of_origin: e.target.value })}
                    className={`w-full p-3 border rounded-lg ${getFieldErrorClass('country_of_origin')}`}
                    data-testid="intake-origin-select"
                  >
                    <option value="">{t('residencePrompt.originPlaceholder')}</option>
                    {countries.map(country => (
                      <option key={country.code || country} value={country.name || country}>{country.name || country}</option>
                    ))}
                  </select>
                  {attemptedNext && validationErrors.country_of_origin && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {validationErrors.country_of_origin}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email (optional)</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Treatment Details */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Treatment Selection</h2>
              
              {/* Show preselected treatment message if applicable */}
              {formData.treatment_categories.length > 0 && (
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Treatment pre-selected based on your browsing. You can add more treatments below.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-3">Which treatments are you interested in? *</label>
                <div className={`space-y-2 ${attemptedNext && validationErrors.treatment_categories ? 'ring-2 ring-red-200 rounded-lg p-2' : ''}`}>
                  {treatmentCategories.map(treatment => (
                    <button
                      key={treatment}
                      type="button"
                      onClick={() => toggleTreatment(treatment)}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        formData.treatment_categories.includes(treatment)
                          ? 'border-blue-600 bg-blue-50'
                          : attemptedNext && validationErrors.treatment_categories
                            ? 'border-red-300 hover:border-red-400'
                            : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{treatment}</span>
                        {formData.treatment_categories.includes(treatment) && (
                          <CheckCircle className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                {attemptedNext && validationErrors.treatment_categories && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {validationErrors.treatment_categories}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tell us about your expectations</label>
                <Textarea
                  value={formData.expectations}
                  onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                  placeholder="What are you hoping to achieve with this treatment?"
                  rows={4}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Previous surgeries or treatments?</label>
                <Textarea
                  value={formData.previous_surgeries}
                  onChange={(e) => setFormData({ ...formData, previous_surgeries: e.target.value })}
                  placeholder="List any previous surgeries or treatments (if any)"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 3: Health Questions */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Health Information</h2>
              <p className="text-sm text-gray-600">This helps clinics assess your eligibility for procedures</p>
              
              <div>
                <label className="block text-sm font-medium mb-3">Smoking Status *</label>
                <div className={`grid grid-cols-2 gap-3 ${attemptedNext && validationErrors.smoking_status ? 'ring-2 ring-red-200 rounded-lg p-1' : ''}`}>
                  {smokingOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, smoking_status: option.value })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.smoking_status === option.value
                          ? 'border-blue-600 bg-blue-50'
                          : attemptedNext && validationErrors.smoking_status
                            ? 'border-red-300 hover:border-red-400'
                            : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {attemptedNext && validationErrors.smoking_status && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {validationErrors.smoking_status}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-3">Do you have any of these conditions? *</label>
                <div className={`space-y-2 ${attemptedNext && validationErrors.chronic_diseases ? 'ring-2 ring-red-200 rounded-lg p-2' : ''}`}>
                  {chronicDiseaseOptions.map(disease => (
                    <button
                      key={disease}
                      type="button"
                      onClick={() => toggleChronicDisease(disease)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        formData.chronic_diseases.includes(disease)
                          ? 'border-blue-600 bg-blue-50'
                          : attemptedNext && validationErrors.chronic_diseases
                            ? 'border-red-300 hover:border-red-400'
                            : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{disease}</span>
                        {formData.chronic_diseases.includes(disease) && (
                          <CheckCircle className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                {attemptedNext && validationErrors.chronic_diseases && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {validationErrors.chronic_diseases}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Current Medications</label>
                <Textarea
                  value={formData.medications}
                  onChange={(e) => setFormData({ ...formData, medications: e.target.value })}
                  placeholder="List any medications you're currently taking"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">BMI (if known)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.bmi}
                    onChange={(e) => setFormData({ ...formData, bmi: e.target.value })}
                    placeholder="25.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Allergies</label>
                  <Input
                    value={formData.allergies}
                    onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                    placeholder="Any known allergies"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Package Type & Travel Preferences */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Service Package</h2>
              
              <div>
                <label className="block text-sm font-medium mb-3">Select your preferred packages (multi-select) *</label>
                <div className={`space-y-3 ${attemptedNext && validationErrors.package_type ? 'ring-2 ring-red-200 rounded-lg p-2' : ''}`}>
                  {packageTypes.map(pkg => {
                    const isSelected = formData.package_type.includes(pkg.value);
                    return (
                      <button
                        key={pkg.value}
                        type="button"
                        onClick={() => {
                          const current = formData.package_type;
                          const updated = isSelected
                            ? current.filter(v => v !== pkg.value)
                            : [...current, pkg.value];
                          setFormData({ ...formData, package_type: updated });
                        }}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : attemptedNext && validationErrors.package_type
                              ? 'border-red-300 hover:border-red-400'
                              : 'border-gray-200 hover:border-blue-300'
                        }`}
                        data-testid={`package-select-${pkg.value}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">{pkg.icon}</div>
                          <div className="flex-1">
                            <div className="font-semibold text-lg">{pkg.label}</div>
                            <div className="text-sm text-gray-600">{pkg.desc}</div>
                          </div>
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckCircle className="h-4 w-4 text-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {attemptedNext && validationErrors.package_type && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {validationErrors.package_type}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Preferred Treatment Location</label>
                <select
                  value={formData.preferred_country}
                  onChange={(e) => setFormData({ ...formData, preferred_country: e.target.value })}
                  className="w-full p-3 border rounded-lg"
                  data-testid="preferred-location-select"
                >
                  <option value="">No preference</option>
                  {/* Clinic countries first, then full list */}
                  <optgroup label="Popular Destinations">
                    {clinicCountriesList.map(c => (
                      <option key={`clinic-${c}`} value={c}>{c}</option>
                    ))}
                  </optgroup>
                  <optgroup label="All Countries">
                    {countries.map(c => (
                      <option key={c.code || c} value={c.name || c}>{c.name || c}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Budget Range</label>
                <select
                  value={formData.budget_range}
                  onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                  className="w-full p-3 border rounded-lg"
                >
                  <option value="">Select your budget</option>
                  {budgetRanges.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Earliest Available Date</label>
                  <DateField
                    value={formData.earliest_date}
                    onChange={(iso) => setFormData({ ...formData, earliest_date: iso })}
                    data-testid="intake-earliest-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Latest Date</label>
                  <DateField
                    value={formData.latest_date}
                    onChange={(iso) => setFormData({ ...formData, latest_date: iso })}
                    data-testid="intake-latest-date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Days Available for Treatment & Recovery</label>
                <Input
                  type="number"
                  value={formData.days_available}
                  onChange={(e) => setFormData({ ...formData, days_available: e.target.value })}
                  placeholder="e.g., 10-14 days"
                />
              </div>
            </div>
          )}

          {/* Step 5: Photo Upload */}
          {step === 5 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Upload Photos</h2>
              <p className="text-sm text-gray-600">
                Photos help clinics provide more accurate quotes. You can upload up to 5 photos.
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-sm text-gray-600 mb-4">
                  Click to upload or drag and drop<br />
                  PNG, JPG up to 5MB each
                </p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload">
                  <Button type="button" variant="outline" asChild>
                    <span>Select Photos</span>
                  </Button>
                </label>
              </div>

              {uploadedPhotos.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  {uploadedPhotos.map(photo => (
                    <div key={photo.id} className="relative group">
                      <img 
                        src={photo.dataUrl} 
                        alt={photo.name}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(photo.id)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <p className="text-xs text-gray-600 mt-1 truncate">{photo.name}</p>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                Optional: You can skip this step and upload photos later
              </p>
            </div>
          )}

          {/* Step 6: Review & Consent */}
          {step === 6 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Review Your Information</h2>
              
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Basic Info</h3>
                  <p>{formData.name}, {formData.age} years old, {formData.gender}</p>
                  <p data-testid="intake-review-residence">{t('residencePrompt.label')}: {countries.find(c => c.code === residenceCountry || c.name === residenceCountry)?.name || residenceCountry}</p>
                  <p data-testid="intake-review-origin">{t('residencePrompt.originLabel')}: {formData.country_of_origin}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Treatments</h3>
                  <p>{formData.treatment_categories.join(', ')}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Package</h3>
                  <p>{Array.isArray(formData.package_type) ? formData.package_type.map(v => packageTypes.find(p => p.value === v)?.label).filter(Boolean).join(', ') : 'Not selected'}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Health</h3>
                  <p>Smoking: {formData.smoking_status}</p>
                  <p>Conditions: {formData.chronic_diseases.join(', ')}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Photos</h3>
                  <p>{uploadedPhotos.length} photo(s) uploaded</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Final Confirmation */}
          {step === 7 && (
            <div className="space-y-6 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Almost There!</h2>
              <p className="text-gray-600">
                {isNewQuoteRequest
                  ? 'Ready to find clinics for your new treatment request?'
                  : 'Ready to find the perfect clinic for you?'
                }
              </p>
              <div className="bg-blue-50 p-4 rounded-lg text-sm text-left">
                <p className="font-semibold mb-2">Next Steps:</p>
                <ol className="list-decimal list-inside space-y-1 text-gray-700">
                  <li>Browse and select clinics that match your criteria</li>
                  <li>Receive personalized offers from selected clinics</li>
                  <li>Compare offers and choose the best option</li>
                  <li>Complete booking and payment</li>
                </ol>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            {step > 1 && step < 7 && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={loading}
                data-testid="intake-back-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}

            {step < totalSteps - 1 && (
              <Button
                onClick={handleNext}
                disabled={loading || !isStepValid}
                data-testid="intake-continue-btn"
                className={`${step === 1 ? 'ml-auto' : ''} ${!isStepValid ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Continue
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}

            {step === totalSteps - 1 && (
              <Button
                onClick={() => setStep(7)}
                disabled={loading}
                data-testid="intake-review-btn"
              >
                Review
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}

            {step === 7 && (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="intake-submit-btn"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {loading ? 'Saving...' : 'Find Matching Clinics'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PatientIntakeEnhanced;